import React, { useState, useEffect, useRef } from 'react';
import styles from './AddressSelector.module.css';
import provincesData from '../areas/provinces.json';
import citiesData from '../areas/cities.json';
import areasData from '../areas/areas.json';
import streetsData from '../areas/streets.json';

export interface DivisionItem {
  code: string;
  name: string;
  provinceCode?: string;
  cityCode?: string;
  areaCode?: string;
}

export interface AddressData {
  province: string;
  city: string;
  district: string;
  street: string;
  detail: string;
}

export interface AddressSelectorProps {
  value: AddressData;
  onChange: (address: AddressData) => void;
  disabled?: boolean;
  placeholder?: {
    province?: string;
    city?: string;
    district?: string;
    street?: string;
    detail?: string;
  };
}

const AddressSelector: React.FC<AddressSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = {
    province: '请选择省份',
    city: '请选择城市',
    district: '请选择区县',
    street: '请选择街道',
    detail: '请输入详细地址'
  }
}) => {
  const [provinces, setProvinces] = useState<DivisionItem[]>([]);
  const [cities, setCities] = useState<DivisionItem[]>([]);
  const [districts, setDistricts] = useState<DivisionItem[]>([]);
  const [streets, setStreets] = useState<DivisionItem[]>([]);
  const [selectedProvince, setSelectedProvince] = useState<string>(value.province || '');
  const [selectedCity, setSelectedCity] = useState<string>(value.city || '');
  const [selectedDistrict, setSelectedDistrict] = useState<string>(value.district || '');
  const [selectedStreet, setSelectedStreet] = useState<string>(value.street || '');
  const [detailAddress, setDetailAddress] = useState<string>(value.detail || '');
  
  // 使用ref来避免不必要的onChange触发
  const lastNotifiedAddress = useRef<AddressData | null>(null);
  const isInitializing = useRef<boolean>(false);

  // 初始化省份数据
  useEffect(() => {
    setProvinces(provincesData);
  }, []);

  // 当外部value变化时，更新内部状态并加载相应的数据
  useEffect(() => {
    const initializeFromValue = async () => {
      console.log('AddressSelector 接收到新的 value:', value);
      
      isInitializing.current = true;
      
      // 更新选中状态
      setSelectedProvince(value.province || '');
      setSelectedCity(value.city || '');
      setSelectedDistrict(value.district || '');
      setSelectedStreet(value.street || '');
      setDetailAddress(value.detail || '');

      // 如果有省份，加载城市数据
      if (value.province && provinces.length > 0) {
        const provinceCode = provinces.find(p => p.name === value.province)?.code;
        if (provinceCode) {
          const cityData = citiesData.filter(c => c.provinceCode === provinceCode);
          setCities(cityData);

          // 如果有城市，加载区县数据
          if (value.city) {
            const cityCode = cityData.find(c => c.name === value.city)?.code;
            if (cityCode) {
              const districtData = areasData.filter(a => a.cityCode === cityCode);
              setDistricts(districtData);

              // 如果有区县，加载街道数据
              if (value.district) {
                const districtCode = districtData.find(d => d.name === value.district)?.code;
                if (districtCode) {
                  const streetData = streetsData.filter(s => s.areaCode === districtCode);
                  setStreets(streetData);
                }
              }
            }
          }
        }
      }
      
      // 初始化完成
      setTimeout(() => {
        isInitializing.current = false;
      }, 100);
    };

    initializeFromValue();
  }, [value, provinces]);

  // 当省份改变时，更新城市列表
  useEffect(() => {
    if (selectedProvince && provinces.length > 0) {
      const provinceCode = provinces.find(p => p.name === selectedProvince)?.code;
      if (provinceCode) {
        const cityData = citiesData.filter(c => c.provinceCode === provinceCode);
        setCities(cityData);
        
        // 只有当城市不在新的城市列表中时才清空
        if (selectedCity && !cityData.find(c => c.name === selectedCity)) {
          setSelectedCity('');
          setSelectedDistrict('');
          setSelectedStreet('');
          setDistricts([]);
          setStreets([]);
        }
      }
    } else if (!selectedProvince) {
      setCities([]);
      setSelectedCity('');
      setSelectedDistrict('');
      setSelectedStreet('');
      setDistricts([]);
      setStreets([]);
    }
  }, [selectedProvince, provinces, selectedCity]);

  // 当城市改变时，更新区县列表
  useEffect(() => {
    if (selectedCity && cities.length > 0) {
      const cityCode = cities.find(c => c.name === selectedCity)?.code;
      if (cityCode) {
        const districtData = areasData.filter(a => a.cityCode === cityCode);
        setDistricts(districtData);
        
        // 只有当区县不在新的区县列表中时才清空
        if (selectedDistrict && !districtData.find(d => d.name === selectedDistrict)) {
          setSelectedDistrict('');
          setSelectedStreet('');
          setStreets([]);
        }
      }
    } else if (!selectedCity) {
      setDistricts([]);
      setSelectedDistrict('');
      setSelectedStreet('');
      setStreets([]);
    }
  }, [selectedCity, cities, selectedDistrict]);

  // 当区县改变时，更新街道列表
  useEffect(() => {
    if (selectedDistrict && districts.length > 0) {
      const districtCode = districts.find(d => d.name === selectedDistrict)?.code;
      if (districtCode) {
        const streetData = streetsData.filter(s => s.areaCode === districtCode);
        setStreets(streetData);
        
        // 只有当街道不在新的街道列表中时才清空
        if (selectedStreet && !streetData.find(s => s.name === selectedStreet)) {
          setSelectedStreet('');
        }
      }
    } else if (!selectedDistrict) {
      setStreets([]);
      setSelectedStreet('');
    }
  }, [selectedDistrict, districts, selectedStreet]);

  // 当任何选择改变时，通知父组件
  useEffect(() => {
    const newAddress = {
      province: selectedProvince,
      city: selectedCity,
      district: selectedDistrict,
      street: selectedStreet,
      detail: detailAddress
    };
    
    // 检查是否与上次通知的地址相同
    const lastAddress = lastNotifiedAddress.current;
    const hasChanged = !lastAddress ||
      newAddress.province !== lastAddress.province ||
      newAddress.city !== lastAddress.city ||
      newAddress.district !== lastAddress.district ||
      newAddress.street !== lastAddress.street ||
      newAddress.detail !== lastAddress.detail;
    
    if (hasChanged && !isInitializing.current) {
      lastNotifiedAddress.current = newAddress;
      onChange(newAddress);
    }
  }, [selectedProvince, selectedCity, selectedDistrict, selectedStreet, detailAddress, onChange]);

  // 处理省份选择
  const handleProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProvince(e.target.value);
  };

  // 处理城市选择
  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCity(e.target.value);
  };

  // 处理区县选择
  const handleDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDistrict(e.target.value);
  };

  // 处理街道选择
  const handleStreetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStreet(e.target.value);
  };

  // 处理详细地址输入
  const handleDetailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDetailAddress(newValue);
  };

  return (
    <div className={styles.addressSelector}>
      <div className={styles.cascadeRow}>
        <div className={styles.selectGroup}>
          <select
            value={selectedProvince}
            onChange={handleProvinceChange}
            disabled={disabled}
            className={`${styles.select} ${!selectedProvince ? styles.placeholder : ''}`}
          >
            <option value="">{placeholder.province}</option>
            {provinces.map(p => (
              <option key={p.code} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.selectGroup}>
          <select
            value={selectedCity}
            onChange={handleCityChange}
            disabled={disabled || !selectedProvince}
            className={`${styles.select} ${!selectedCity ? styles.placeholder : ''}`}
          >
            <option value="">{placeholder.city}</option>
            {cities.map(c => (
              <option key={c.code} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.selectGroup}>
          <select
            value={selectedDistrict}
            onChange={handleDistrictChange}
            disabled={disabled || !selectedCity}
            className={`${styles.select} ${!selectedDistrict ? styles.placeholder : ''}`}
          >
            <option value="">{placeholder.district}</option>
            {districts.map(d => (
              <option key={d.code} value={d.name}>
                {d.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.selectGroup}>
          <select
            value={selectedStreet}
            onChange={handleStreetChange}
            disabled={disabled || !selectedDistrict}
            className={`${styles.select} ${!selectedStreet ? styles.placeholder : ''}`}
          >
            <option value="">{placeholder.street}</option>
            {streets.map(s => (
              <option key={s.code} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.detailRow}>
        <div className={styles.inputGroup}>
          <svg className={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          <input
            key="detail-address-input"
            type="text"
            value={detailAddress}
            onChange={handleDetailChange}
            disabled={disabled}
            placeholder={placeholder.detail}
            className={styles.detailInput}
          />
        </div>
      </div>
    </div>
  );
};

export default AddressSelector;
